import React, { createContext, useState, useEffect, useContext } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "../config/firebase";
import { doc, onSnapshot } from "firebase/firestore";

export const AuthContext = createContext(/** @type {any} */ (null));

export const useAuth = () => useContext(AuthContext);

/** @param {{ children: React.ReactNode }} props */
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(/** @type {any} */ (null));
  const [companyInfo, setCompanyInfo] = useState(/** @type {any} */ (null));
  const [userInfo, setUserInfo] = useState(/** @type {any} */ (null));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeProfile = () => {};
    let unsubscribeCompany = () => {};
    let unsubscribeInternal = () => {};

    // Função auxiliar para evitar memory leaks ao deslogar
    const cleanupListeners = () => {
      unsubscribeProfile();
      unsubscribeCompany();
      unsubscribeInternal();
    };

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      setCurrentUser(user);

      if (user) {
        // 1. BYPASS SUPER ADMIN (Sócio / Conta Mestra)
        if (user.email === 'kadoflorence@gmail.com') {
          setUserInfo({
            email: user.email,
            uid: user.uid,
            name: "Ricardo (SuperAdmin)",
            role: "superadmin", 
            isInternal: true
          });
          setCompanyInfo(null);
          setLoading(false);
          return;
        }

        // 2. O PULO DO GATO: Listener em tempo real resolve "Race Condition" e "Ghost Sessions"
        unsubscribeProfile = onSnapshot(doc(db, "userProfiles", user.uid), (userDoc) => {
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserInfo(userData);

            // BUSCA DADOS DA IMOBILIÁRIA VINCULADA EM TEMPO REAL (Gatilho Anti-Ghost Session)
            if (userData.companyId) {
              unsubscribeCompany(); // Limpa listener anterior se houver
              unsubscribeCompany = onSnapshot(doc(db, "companies", userData.companyId), (compDoc) => {
                if (compDoc.exists()) {
                  setCompanyInfo({ id: compDoc.id, ...compDoc.data() });
                } else {
                  // A empresa foi deletada do banco enquanto ele navegava. Ejeção total!
                  setCompanyInfo(null);
                  signOut(auth);
                }
              }, (error) => {
                if (error.code !== 'permission-denied') console.error("Erro no listener da imobiliária:", error);
              });
            }
            setLoading(false);
          } else {
            // 3. A ELITE DA REAL BENEFÍCIOS (Equipe Interna) - Agora em Tempo Real!
            unsubscribeInternal(); // Limpa listener anterior
            unsubscribeInternal = onSnapshot(doc(db, "users", user.uid), (internalDoc) => {
              if (internalDoc.exists()) {
                const internalData = internalDoc.data();
                setUserInfo({ 
                  ...internalData, 
                  isInternal: true, 
                  role: internalData.role === 'master' ? 'superadmin' : internalData.role 
                });
                setCompanyInfo(null);
                setLoading(false);
              } else {
                // Usuário recém-criado ou deletado. Mantemos em pending.
                // Se Firebase Auth revogar o token por exclusão, o onAuthStateChanged assume a queda.
                setUserInfo({ uid: user.uid, email: user.email, isPending: true });
                setLoading(false);
              }
            }, (error) => {
               if (error.code !== 'permission-denied') console.error("Erro no listener de equipe interna:", error);
               setLoading(false);
            });
          }
        }, (error) => {
          if (error.code !== 'permission-denied') console.error("Erro no listener de perfil:", error);
          setLoading(false);
        });

      } else {
        setCompanyInfo(null);
        setUserInfo(null);
        cleanupListeners();
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      cleanupListeners();
    };
  }, []);

  const logout = () => signOut(auth);

  const value = { currentUser, companyInfo, userInfo, loading, logout };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};